/**
 * useServiceOrderDetailsRealtime - Tempo real do detalhe da O.S. aberto.
 *
 * Escuta alterações da própria O.S. e dos registros ligados a ela (andamentos,
 * serviços e anexos gravados pelo app do prestador ou por outro usuário) e
 * recarrega detalhes e andamentos sem F5. Um canal por O.S. aberta; RLS
 * restringe ao tenant ativo.
 *
 * RELEASE: 2026-10-18 v1.1.0 - Escuta também service_order_events,
 *          service_order_services e service_order_attachments; avisos em rajada
 *          agrupados (400 ms) antes de recarregar.
 * RELEASE: 2026-10-17 v1.0.0 - Versão inicial.
 */
import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

const logger = createLogger('ServiceOrderDetailsRealtime');

/** Tabelas filhas da O.S. que geram atualização na tela. */
const CHILD_TABLES = ['service_order_events', 'service_order_services', 'service_order_attachments'] as const;

export function useServiceOrderDetailsRealtime(serviceOrderId: string | null | undefined, enabled = true) {
  const qc = useQueryClient();
  useEffect(() => {
    if (!enabled || !serviceOrderId) return;
    let timer: ReturnType<typeof setTimeout> | null = null;
    // Agrupa vários avisos seguidos em uma única recarga
    const refresh = () => {
      if (timer) clearTimeout(timer);
      // Prefixo cobre detalhes e andamentos (chaves ['service-orders','details'|'timeline',hint,id])
      timer = setTimeout(() => qc.invalidateQueries({ queryKey: ['service-orders'] }), 400);
    };
    let channel = supabase
      .channel(`service-order-details-${serviceOrderId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'service_orders', filter: `id=eq.${serviceOrderId}` }, refresh);
    for (const table of CHILD_TABLES) {
      channel = channel.on('postgres_changes', { event: '*', schema: 'public', table, filter: `service_order_id=eq.${serviceOrderId}` }, refresh);
    }
    channel.subscribe((status) => { if (status === 'CHANNEL_ERROR') logger.warn('Falha no canal da O.S.'); });
    return () => { if (timer) clearTimeout(timer); supabase.removeChannel(channel); };
  }, [enabled, serviceOrderId, qc]);
}
